import { affiliateStatuses, type AffiliateStatus } from '@buyerflo/core/schemas/affiliate'
import { Field } from '@buyerflo/ui/components/FormSection'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import Button from 'components/button'
import CopyButton from 'components/CopyButton'
import { CheckRounded } from 'components/icons'
import Modal from 'components/modal'
import { useShowSnack } from 'components/snackbar'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Input, SizableText, XStack, YStack } from 'tamagui'
import { useTRPC, useTRPCClient } from 'utils/trpc'
import z from 'zod'

type AffiliateRow = {
    affiliateId: string
    name: string
    email: string
    status: string
    promoCode: string
    discountPercent: number
    discountDurationMonths: number
    commissionPercent: number
    commissionDurationMonths: number
    abn?: string
}

// Numeric fields keep their RAW STRING value so the user can clear them while typing without the
// box snapping back to "0" (which would silently fail the server's min(1)/min(0) rules). The Zod
// resolver parses + range-checks each on submit. Month fields are integer-only; percent allows
// decimals.
const numericField = (
    label: string,
    { min, max, integer }: { min: number; max: number; integer?: boolean },
) =>
    z
        .string()
        .trim()
        .min(1, `${label} is required`)
        .refine(s => Number.isFinite(Number(s)), `${label} must be a valid number`)
        .refine(s => !integer || Number.isInteger(Number(s)), `${label} must be a whole number`)
        .refine(s => Number(s) >= min && Number(s) <= max, `${label} must be between ${min} and ${max}`)

// One schema for both modes — the create-only (email/promoCode/abn) and edit-only checks are
// enforced in the superRefine keyed on `isNew`, mirroring the server's Create/UpdateAffiliateInput.
const makeFormSchema = (isNew: boolean) =>
    z
        .object({
            email: z.string().trim(),
            name: z.string(),
            promoCode: z.string().trim(),
            discountPercent: numericField('Discount %', { min: 0, max: 100 }),
            discountMonths: numericField('Discount months', { min: 1, max: 36, integer: true }),
            commissionPercent: numericField('Commission %', { min: 0, max: 100 }),
            commissionMonths: numericField('Commission window', { min: 1, max: 60, integer: true }),
            abn: z.string(),
            status: z.enum(affiliateStatuses),
        })
        .superRefine((v, ctx) => {
            const cleanAbn = v.abn.replace(/\s+/g, '')
            if (isNew) {
                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email))
                    ctx.addIssue({ path: ['email'], code: 'custom', message: 'A valid email is required' })
                if (!/^[A-Z0-9]{3,40}$/.test(v.promoCode))
                    ctx.addIssue({
                        path: ['promoCode'],
                        code: 'custom',
                        message: 'Code must be 3–40 letters or numbers',
                    })
                if (!/^\d{11}$/.test(cleanAbn))
                    ctx.addIssue({ path: ['abn'], code: 'custom', message: 'A valid 11-digit ABN is required' })
            } else if (cleanAbn && !/^\d{11}$/.test(cleanAbn)) {
                ctx.addIssue({ path: ['abn'], code: 'custom', message: 'ABN must be 11 digits' })
            }
        })

type FormValues = z.infer<ReturnType<typeof makeFormSchema>>
type NumericName = 'discountPercent' | 'discountMonths' | 'commissionPercent' | 'commissionMonths'

const FieldError = ({ message }: { message?: string }) =>
    message ? (
        <SizableText
            size="$1"
            color="$statusDanger"
        >
            {message}
        </SizableText>
    ) : null

export default function EditAffiliateModal({
    affiliate,
    open,
    onClose,
    onSaved,
}: {
    affiliate?: AffiliateRow
    open: boolean
    onClose: () => void
    onSaved: () => void
}) {
    const isNew = !affiliate
    const trpc = useTRPC()
    const client = useTRPCClient()
    const showSnack = useShowSnack()

    const createMutation = useMutation(trpc.affiliateAdmin.createAffiliate.mutationOptions())
    const updateMutation = useMutation(trpc.affiliateAdmin.updateAffiliate.mutationOptions())

    const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
        resolver: zodResolver(useMemo(() => makeFormSchema(isNew), [isNew])),
        defaultValues: {
            email: affiliate?.email ?? '',
            name: affiliate?.name ?? '',
            promoCode: affiliate?.promoCode ?? '',
            discountPercent: String(affiliate?.discountPercent ?? 10),
            discountMonths: String(affiliate?.discountDurationMonths ?? 6),
            commissionPercent: String(affiliate?.commissionPercent ?? 20),
            commissionMonths: String(affiliate?.commissionDurationMonths ?? 12),
            abn: affiliate?.abn ?? '',
            status: (affiliate?.status as AffiliateStatus) ?? 'active',
        },
    })

    // create-only user lookup
    const [userId, setUserId] = useState<string | undefined>()
    const [lookingUp, setLookingUp] = useState(false)

    const name = watch('name')
    const email = watch('email')
    const status = watch('status')

    // ABN verification against the Australian Business Register. The looked-up name is tied to the
    // exact ABN it was fetched for, so editing the ABN auto-invalidates the confirmation (derived
    // `abnConfirmed` below) without any extra clearing logic.
    const [abnLookup, setAbnLookup] = useState<{ abn: string; name: string; status?: string } | null>(null)
    const [lookingUpAbn, setLookingUpAbn] = useState(false)
    const cleanAbn = watch('abn').replace(/\s+/g, '')
    const abnConfirmed = !!abnLookup && abnLookup.abn === cleanAbn

    const [created, setCreated] = useState<{ promoCode: string; referralLink?: string } | null>(null)

    const lookup = async () => {
        setLookingUp(true)
        try {
            const found = await client.affiliateAdmin.findUserByEmail.query({ email: email.trim() })
            if (!found) {
                showSnack('No Buyerflo user with that email', 'danger')
                setUserId(undefined)
                return
            }
            setUserId(found.userId)
            setValue('name', found.name)
            showSnack(`Found ${found.name || found.email}`, 'success')
        } catch {
            showSnack('Lookup failed', 'danger')
        } finally {
            setLookingUp(false)
        }
    }

    const lookupAbn = async () => {
        if (!/^\d{11}$/.test(cleanAbn)) {
            showSnack('Enter an 11-digit ABN first', 'danger')
            return
        }
        setLookingUpAbn(true)
        try {
            const result = await client.affiliateAdmin.lookupAbn.query({ abn: cleanAbn })
            setAbnLookup({ abn: result.abn, name: result.entityName, status: result.status })
        } catch (e) {
            setAbnLookup(null)
            showSnack(e instanceof Error ? e.message : 'ABN lookup failed', 'danger')
        } finally {
            setLookingUpAbn(false)
        }
    }

    // Field-level validation (required/range/integer/format) lives in the resolver. The checks here
    // are the async, cross-field gates that aren't plain field values: a looked-up user id and an
    // ABN verified against the business register.
    const onSubmit = async (data: FormValues) => {
        const dp = Number(data.discountPercent)
        const dm = Number(data.discountMonths)
        const cp = Number(data.commissionPercent)
        const cm = Number(data.commissionMonths)
        try {
            if (isNew) {
                if (!userId) {
                    showSnack('Look up a user by email first', 'danger')
                    return
                }
                if (!abnConfirmed) {
                    showSnack('Verify the ABN and confirm the business name first', 'danger')
                    return
                }
                const result = await createMutation.mutateAsync({
                    userId,
                    name: data.name,
                    email: data.email.trim(),
                    promoCode: data.promoCode.toUpperCase(),
                    discountPercent: dp,
                    discountDurationMonths: dm,
                    commissionPercent: cp,
                    commissionDurationMonths: cm,
                    abn: cleanAbn,
                })
                setCreated({ promoCode: result.promoCode, referralLink: result.referralLink })
                showSnack('Affiliate created', 'success')
            } else {
                const originalAbn = (affiliate!.abn ?? '').replace(/\s+/g, '')
                if (cleanAbn && cleanAbn !== originalAbn && !abnConfirmed) {
                    showSnack('Verify the new ABN and confirm the business name first', 'danger')
                    return
                }
                await updateMutation.mutateAsync({
                    affiliateId: affiliate!.affiliateId,
                    name: data.name,
                    status: data.status,
                    discountPercent: dp,
                    discountDurationMonths: dm,
                    commissionPercent: cp,
                    commissionDurationMonths: cm,
                    abn: cleanAbn || undefined,
                })
                showSnack('Affiliate updated', 'success')
                onSaved()
            }
        } catch (e) {
            showSnack(e instanceof Error ? e.message : 'Save failed', 'danger')
        }
    }

    // Render helper (NOT a component — defining a component inline would remount it every keystroke
    // and drop input focus). Returns a Controller-bound numeric cell.
    const numericCell = (fieldName: NumericName, label: string, suffix?: string, integer?: boolean) => (
        <Controller
            name={fieldName}
            control={control}
            render={({ field, fieldState }) => (
                <Field
                    flex={1}
                    label={label}
                >
                    <XStack
                        alignItems="center"
                        gap="$2"
                    >
                        <Input
                            flex={1}
                            keyboardType="numeric"
                            value={field.value}
                            onBlur={field.onBlur}
                            onChangeText={t => field.onChange(t.replace(integer ? /[^0-9]/g : /[^0-9.]/g, ''))}
                        />
                        {suffix ? <SizableText color="$colorMuted">{suffix}</SizableText> : null}
                    </XStack>
                    <FieldError message={fieldState.error?.message} />
                </Field>
            )}
        />
    )

    return (
        <Modal
            open={open}
            onOpenChange={o => !o && onClose()}
            width="520px"
        >
            {created ? (
                <YStack gap="$4">
                    <SizableText
                        fontSize="$6"
                        fontWeight="700"
                    >
                        Affiliate created
                    </SizableText>
                    <Field label="Promo code">
                        <XStack
                            gap="$2"
                            alignItems="center"
                        >
                            <SizableText
                                fontFamily="$mono"
                                style={{ fontSize: 18 }}
                                flex={1}
                            >
                                {created.promoCode}
                            </SizableText>
                            <CopyButton
                                text={created.promoCode}
                                variant="outlined"
                            />
                        </XStack>
                    </Field>
                    {created.referralLink && (
                        <Field label="Referral link">
                            <YStack gap="$2">
                                <SizableText
                                    size="$2"
                                    color="$colorSoft"
                                    backgroundColor="$backgroundHover"
                                    padding="$2"
                                    borderRadius="$3"
                                >
                                    {created.referralLink}
                                </SizableText>
                                <CopyButton
                                    text={created.referralLink}
                                    label="Copy link"
                                />
                            </YStack>
                        </Field>
                    )}
                    <Button onPress={onSaved}>Done</Button>
                </YStack>
            ) : (
                <YStack gap="$3.5">
                    <SizableText
                        fontSize="$6"
                        fontWeight="700"
                    >
                        {isNew ? 'Add affiliate' : `Edit ${affiliate!.name}`}
                    </SizableText>

                    {isNew && (
                        <Field
                            label="User email"
                            hint="The existing Buyerflo user to flag as an affiliate"
                        >
                            <Controller
                                name="email"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <YStack gap="$1.5">
                                        <XStack gap="$2">
                                            <Input
                                                flex={1}
                                                value={field.value}
                                                onChangeText={field.onChange}
                                                onBlur={field.onBlur}
                                                placeholder="person@example.com"
                                                autoCapitalize="none"
                                            />
                                            <Button
                                                variant="outlined"
                                                loading={lookingUp}
                                                onPress={lookup}
                                            >
                                                Look up
                                            </Button>
                                        </XStack>
                                        <FieldError message={fieldState.error?.message} />
                                    </YStack>
                                )}
                            />
                        </Field>
                    )}

                    {(name || !isNew) && (
                        <Field label="Name">
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        value={field.value}
                                        onChangeText={field.onChange}
                                        onBlur={field.onBlur}
                                    />
                                )}
                            />
                        </Field>
                    )}

                    {isNew && (
                        <Field
                            label="Promo code"
                            hint="Used in their referral link (?ref=CODE)"
                        >
                            <Controller
                                name="promoCode"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <YStack gap="$1.5">
                                        <Input
                                            value={field.value}
                                            onChangeText={t => field.onChange(t.toUpperCase())}
                                            onBlur={field.onBlur}
                                            autoCapitalize="characters"
                                            placeholder="JANE10"
                                        />
                                        <FieldError message={fieldState.error?.message} />
                                    </YStack>
                                )}
                            />
                        </Field>
                    )}

                    <XStack gap="$3">
                        {numericCell('discountPercent', 'Discount %', '%')}
                        {numericCell('discountMonths', 'Discount months', 'mo', true)}
                    </XStack>
                    <XStack gap="$3">
                        {numericCell('commissionPercent', 'Commission %', '%')}
                        {numericCell('commissionMonths', 'Commission window', 'mo', true)}
                    </XStack>

                    <Field
                        label="ABN"
                        hint="11-digit Australian Business Number — verify it against the business register before payouts can run"
                    >
                        <Controller
                            name="abn"
                            control={control}
                            render={({ field, fieldState }) => (
                                <YStack gap="$1.5">
                                    <XStack gap="$2">
                                        <Input
                                            flex={1}
                                            value={field.value}
                                            onChangeText={field.onChange}
                                            onBlur={field.onBlur}
                                            keyboardType="numeric"
                                        />
                                        <Button
                                            variant="outlined"
                                            loading={lookingUpAbn}
                                            onPress={lookupAbn}
                                        >
                                            Verify
                                        </Button>
                                    </XStack>
                                    <FieldError message={fieldState.error?.message} />
                                </YStack>
                            )}
                        />
                        {abnConfirmed && (
                            <XStack
                                gap="$2"
                                alignItems="center"
                                marginTop="$2"
                            >
                                <CheckRounded
                                    size={16}
                                    color="$green10"
                                />
                                <YStack flex={1}>
                                    <SizableText
                                        size="$3"
                                        fontWeight="600"
                                    >
                                        {abnLookup!.name}
                                    </SizableText>
                                    {abnLookup!.status && abnLookup!.status !== 'Active' && (
                                        <SizableText
                                            size="$1"
                                            color="$red10"
                                        >
                                            ABN status: {abnLookup!.status}
                                        </SizableText>
                                    )}
                                </YStack>
                            </XStack>
                        )}
                    </Field>

                    {!isNew && (
                        <Field label="Status">
                            <XStack
                                gap="$2"
                                flexWrap="wrap"
                            >
                                {affiliateStatuses.map(s => (
                                    <Button
                                        key={s}
                                        size="$2"
                                        variant={status === s ? undefined : 'outlined'}
                                        onPress={() => setValue('status', s)}
                                    >
                                        {s}
                                    </Button>
                                ))}
                            </XStack>
                        </Field>
                    )}

                    <XStack
                        gap="$2"
                        justifyContent="flex-end"
                        marginTop="$2"
                    >
                        <Button
                            variant="outlined"
                            onPress={onClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            loading={createMutation.isPending || updateMutation.isPending}
                            onPress={handleSubmit(onSubmit)}
                        >
                            {isNew ? 'Create' : 'Save'}
                        </Button>
                    </XStack>
                </YStack>
            )}
        </Modal>
    )
}
